/**
 * Deleted Sections Modal - View and restore deleted sections
 * Story 232: View history of changes and undo them
 *
 * Shows a list of deleted sections for a document with restore capability.
 */

import { useCallback } from 'react';
import { useDeletedSections, useRestoreSection } from '../../hooks/useSectionHistory';
import type { DeletedSectionItem } from '../../types/history';

/** Time formatting options */
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

interface DeletedSectionsModalProps {
  projectSlug: string;
  storyId: string;
  docType: string;
  /** Called when modal should close */
  onClose: () => void;
  /** Called when a section is restored */
  onRestore?: () => void;
}

export function DeletedSectionsModal({
  projectSlug,
  storyId,
  docType,
  onClose,
  onRestore,
}: DeletedSectionsModalProps) {
  // Fetch deleted sections
  const { data, isLoading, error } = useDeletedSections(projectSlug, storyId, docType);

  // Restore mutation
  const restoreSection = useRestoreSection(projectSlug, storyId, docType);

  // Format timestamp
  const formatTimestamp = useCallback((isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString(undefined, DATE_FORMAT_OPTIONS);
    } catch {
      return isoString;
    }
  }, []);

  // Handle restore
  const handleRestore = useCallback(
    async (section: DeletedSectionItem) => {
      try {
        const result = await restoreSection.mutateAsync(section.id);

        // Show warnings if any (e.g., anchor renamed due to conflict)
        if (result.warnings?.length) {
          // For now, just log warnings - could use toast notification
          console.log('Restore warnings:', result.warnings);
        }

        // Notify parent
        onRestore?.();
      } catch (err) {
        console.error('Failed to restore section:', err);
      }
    },
    [restoreSection, onRestore]
  );

  const deletedSections = data?.deleted_sections ?? [];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 'var(--z-modal-backdrop, 200)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-base, #fff)',
          borderRadius: 'var(--radius-lg, 8px)',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '70vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-xl, 0 20px 25px -5px rgba(0, 0, 0, 0.1))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid var(--border-light, #e2e8f0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: 600,
                color: 'var(--text-primary, #1e293b)',
              }}
            >
              Deleted Sections
            </h2>
            <p
              style={{
                margin: '4px 0 0 0',
                fontSize: '13px',
                color: 'var(--text-muted, #94a3b8)',
              }}
            >
              {docType === 'prd' ? 'PRD' : 'Tech Design'} - Restore deleted sections
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              fontSize: '18px',
              color: 'var(--text-muted, #94a3b8)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px',
              lineHeight: 1,
            }}
            title="Close"
            aria-label="Close deleted sections modal"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '16px 24px',
          }}
        >
          {isLoading ? (
            <div
              style={{
                padding: '24px',
                color: 'var(--text-muted, #94a3b8)',
                textAlign: 'center',
              }}
            >
              Loading deleted sections...
            </div>
          ) : error ? (
            <div
              style={{
                padding: '24px',
                color: 'var(--status-error, #ef4444)',
                textAlign: 'center',
              }}
            >
              Failed to load deleted sections
            </div>
          ) : deletedSections.length === 0 ? (
            <div
              style={{
                padding: '24px',
                color: 'var(--text-muted, #94a3b8)',
                textAlign: 'center',
              }}
            >
              No deleted sections found.
              <br />
              <span style={{ fontSize: '13px' }}>
                Sections you delete will appear here for restoration.
              </span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {deletedSections.map((section) => (
                <div
                  key={section.id}
                  style={{
                    padding: '12px 16px',
                    backgroundColor: 'var(--bg-subtle, #f8fafc)',
                    borderRadius: 'var(--radius-md, 6px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'var(--text-primary, #1e293b)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {section.title}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '4px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          color: 'var(--text-muted, #94a3b8)',
                        }}
                      >
                        {section.section_key}
                      </span>
                      {section.anchor && (
                        <span
                          style={{
                            fontSize: '11px',
                            color: 'var(--text-muted, #94a3b8)',
                            backgroundColor: 'var(--bg-muted, #f1f5f9)',
                            padding: '2px 6px',
                            borderRadius: '3px',
                          }}
                        >
                          {section.anchor}
                        </span>
                      )}
                    </div>
                    {section.deleted_at && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: 'var(--text-muted, #94a3b8)',
                          marginTop: '4px',
                        }}
                      >
                        Deleted {formatTimestamp(section.deleted_at)}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleRestore(section)}
                    disabled={restoreSection.isPending}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      fontWeight: 500,
                      color: 'var(--interactive, #4f46e5)',
                      backgroundColor: 'transparent',
                      border: '1px solid var(--interactive, #4f46e5)',
                      borderRadius: 'var(--radius-sm, 4px)',
                      cursor: restoreSection.isPending ? 'wait' : 'pointer',
                      opacity: restoreSection.isPending ? 0.6 : 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {restoreSection.isPending ? 'Restoring...' : 'Restore'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--border-light, #e2e8f0)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              color: 'var(--text-secondary, #64748b)',
              backgroundColor: 'transparent',
              border: '1px solid var(--border-default, #e2e8f0)',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
